import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { brl } from "./PurchaseDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const Line = ({ l, v, cls = "", testid }) => (
  <div className="flex justify-between gap-3"><span>{l}</span><span className={`font-mono ${cls}`} data-testid={testid}>{v}</span></div>
);

const Summary = ({ amount, fee, net, feePct, pixKey, password, setPassword, busy, locked, onBack, onConfirm }) => (
  <form onSubmit={onConfirm} className="space-y-4 text-sm" data-testid="withdraw-summary">
    <div className="font-display text-lg font-bold">Resumo do saque</div>
    <div className="rounded-xl bg-muted/50 p-4 space-y-2">
      <Line l="Valor solicitado" v={brl(amount)} testid="withdraw-summary-amount" />
      <Line l={`Taxa de saque (${feePct.toLocaleString("pt-BR")}%)`} v={`− ${brl(fee)}`} cls="text-rose-500" testid="withdraw-fee" />
      <div className="border-t border-border pt-2"><Line l="Valor líquido a receber" v={brl(net)} cls="gold-text font-bold" testid="withdraw-net" /></div>
      {pixKey && <Line l="Chave Pix" v={pixKey} testid="withdraw-summary-pix" />}
    </div>
    <p className="text-xs text-muted-foreground">O valor sai do seu saldo agora e o pedido vai para aprovação do administrador. Após aprovado, o Pix é enviado automaticamente.</p>
    <div className="space-y-1.5">
      <Label htmlFor="wd-password">Senha de acesso</Label>
      <Input id="wd-password" type="password" autoComplete="current-password" placeholder="Digite sua senha para confirmar"
             value={password} onChange={(e) => setPassword(e.target.value)} data-testid="withdraw-password-input" />
    </div>
    <div className="flex gap-2">
      <Button type="button" variant="outline" onClick={onBack} data-testid="withdraw-back-btn">Voltar</Button>
      <Button type="submit" className="gold-btn flex-1" disabled={busy || locked || !password} data-testid="withdraw-confirm-btn">
        {busy ? "Enviando..." : locked ? "Saque indisponível no momento" : "Confirmar saque"}
      </Button>
    </div>
  </form>
);

const parseAmount = (v) => {
  let s = String(v).replace(/[^\d.,]/g, "");
  if (s.includes(",")) { s = s.replace(/\./g, "").replace(",", "."); }
  else if ((s.match(/\./g) || []).length > 1 || /\.\d{3}$/.test(s)) { s = s.replace(/\./g, ""); }
  return Math.round((Number(s) || 0) * 100) / 100;
};

export const WithdrawForm = ({ balance, min, feePct, pixKey, canSubmit = true, onDone, onCancel }) => {
  const [value, setValue] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const amount = parseAmount(value);
  const fee = Math.round(amount * feePct) / 100;
  const net = Math.round((amount - fee) * 100) / 100;
  const error = amount && amount < min ? `Valor mínimo: ${brl(min)}` : amount > balance ? "Saldo insuficiente" : "";

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/withdrawals", { amount, password });
      toast.success("Saque solicitado! Aguardando aprovação do administrador.");
      setValue(""); setPassword(""); setStep(1);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (step === 2) {
    return <Summary amount={amount} fee={fee} net={net} feePct={feePct} pixKey={pixKey} password={password} setPassword={setPassword}
                    busy={busy} locked={!canSubmit} onBack={() => setStep(1)} onConfirm={confirm} />;
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); setStep(2); }} className="space-y-4 text-sm" data-testid="withdraw-form">
      <div className="space-y-1.5">
        <Label htmlFor="wd-amount">Quanto você quer sacar?</Label>
        <Input id="wd-amount" inputMode="decimal" placeholder={`Mínimo ${brl(min)}`} value={value}
               onChange={(e) => setValue(e.target.value)} data-testid="withdraw-amount-input" />
        <Button type="button" variant="link" className="px-0 h-auto text-xs gold-text"
                onClick={() => setValue(balance.toFixed(2).replace(".", ","))} data-testid="withdraw-max-btn">Sacar tudo</Button>
      </div>
      {error && <p className="text-xs text-rose-500" data-testid="withdraw-error">{error}</p>}
      <div className="flex gap-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel} data-testid="withdraw-cancel-btn">Cancelar</Button>}
        <Button type="submit" className="gold-btn flex-1" disabled={!amount || !!error} data-testid="withdraw-continue-btn">Ver resumo</Button>
      </div>
    </form>
  );
};
